/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import * as stagesCase from "../../../Stages/StagesCase";
import personFields from "../modules/personFields";
import { dateFieldDefault, instituteReferenceDefault } from "../../../validationRules";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import _ from "lodash";
import { arrayToSelectOptions } from "../../../formServiceFunctions";
import { objectivesLawFirm } from "../modules/objectiveLawfirmValues";
import { FormDefinition } from "../../../../types/ProductService/ProductService";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { userHasOneOfTheseRoles } from "../../../backofficeUserService";
import { BackofficeUser, Roles } from "../../../../types/BackofficeUser";
import { abstractLitigationStageFormDefinition } from "../abstractLitigationCase/stageFormDefinition";
import { ContractUnion } from "../../../../types/ContractUnion";

export function stageFormDefinition(product: ContractUnion, currentUser?: BackofficeUser): ReturnType<FormDefinition<ContractUnion>> {
  const createdFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
  ];

  const dataCompletedFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategory`,
      isDisabled: ({ product }) => product.caseCategory === "advisory",
      options: ({ product }) => {
        return product.caseCategory === "advisory"
          ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
          : [{ value: "enforceClaim" }, { value: "defendClaim" }];
      },

      alert: {
        onChange:
          "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
          "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
          "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
          "der Mandantschaft im Verfahren tatsächlich ändern?",
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.objectiveClient`,
      isDisabled: () => true,
      isHidden: ({ product }) => product.caseCategory !== "enforceClaim",
      options: () => [
        { value: "compensation" },
        { value: "reduction" },
        { value: "replacement" },
        { value: "repair" },
        { value: "withdrawal" },
        { value: "fulfillment" },
        { value: "other" },
      ],
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.defenseProcessStatus`,
      isDisabled: ({ product }) => product.stage! >= stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
      options: () => [
        { value: "reminder" },
        { value: "debtCollection" },
        { value: "paymentOrder" },
        { value: "enforcementOrder" },
        { value: "lawsuit" },
        { value: "none" },
      ],
    },
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.paymentOrderDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.enforcementOrderDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.defectsDescriptionClient`,
      isDisabled: () => true,
      isHidden: ({ product }) =>
        product.advisoryHandling ||
        !_.has(product, "defectsDescriptionLawFirm") ||
        product.caseCategory !== "enforceClaim" ||
        product.isLawsuitHandling ||
        (!product.hasDefects && product.reasonForDispute !== "hasDefects"),
      additionalProps: {
        multiline: true,
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_DATA_COMPLETED],
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.objectiveLawFirm`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment === "reject" || product.isLawsuitHandling,
      options: ({ product }) => {
        if (_.isEmpty(product.possibleObjectives)) {
          return objectivesLawFirm;
        } else {
          return arrayToSelectOptions(product.possibleObjectives);
        }
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.objectiveLawFirmSecondary`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment === "reject" || product.isLawsuitHandling,
      options: ({ product }) => {
        if (_.isEmpty(product.possibleObjectives)) {
          return objectivesLawFirm;
        } else {
          return arrayToSelectOptions(product.possibleObjectives);
        }
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.objectiveLawFirmTertiary`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment === "reject" || product.isLawsuitHandling,
      options: ({ product }) => {
        if (_.isEmpty(product.possibleObjectives)) {
          return objectivesLawFirm;
        } else {
          return arrayToSelectOptions(product.possibleObjectives);
        }
      },
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.claimedAmountLawFirm`,
      isDisabled: ({ product }) => product.stage! >= stagesCase.STAGE_CHECKED,
      isHidden: ({ product }) =>
        product.advisoryHandling || product.isLawsuitHandling,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.defectsDescriptionLawFirm`,
      isHidden: ({ product }) =>
        product.advisoryHandling ||
        !_.has(product, "defectsDescriptionLawFirm") ||
        product.caseCategory !== "enforceClaim" ||
        product.isLawsuitHandling ||
        (!product.hasDefects && product.reasonForDispute !== "hasDefects"),
      additionalProps: {
        multiline: true,
      },
    },
  ];

  const dataLawFirmCompletedFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    ...dataCompletedFields,
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.additionalIssueDescriptionText`,
      isHidden: ({ product }) =>
        product.advisoryHandling || product.isLawsuitHandling,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.additionalClaimText`,
      isHidden: ({ product }) =>
        product.advisoryHandling || product.isLawsuitHandling,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.automaticallyGenerateExtrajudicialLetter`,
      isHidden: ({ product }) => product.caseCategoryAssessment === "reject",
      options: ({ product }) =>
        trueFalseDefaultOptions(`${product.productClassName}.automaticallyGenerateExtrajudicialLetter`),
    },
  ];

  const checkedFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategory`,
      isDisabled: ({ product }) => product.caseCategory === "advisory",
      options: ({ product }) => {
        return product.caseCategory === "advisory"
          ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
          : [{ value: "enforceClaim" }, { value: "defendClaim" }];
      },
      alert: {
        onChange:
          "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
          "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
          "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
          "der Mandantschaft im Verfahren tatsächlich ändern?",
      },
    },
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.additionalIssueDescriptionText`,
      isHidden: ({ product }) => product.advisoryHandling,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.additionalClaimText`,
      isHidden: ({ product }) => product.advisoryHandling,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.defectsDescriptionLawFirm`,
      isHidden: ({ product }) =>
        !_.has(product, "defectsDescriptionLawFirm") || product.caseCategory !== "enforceClaim" || !product.hasDefects,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_CHECKED],
  ];

  const inProgressFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategory`,
      isDisabled: ({ product }) => product.caseCategory === "advisory",
      options: ({ product }) => {
        return product.caseCategory === "advisory"
          ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
          : [{ value: "enforceClaim" }, { value: "defendClaim" }];
      },
      alert: {
        onChange:
          "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
          "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
          "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
          "der Mandantschaft im Verfahren tatsächlich ändern?",
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_IN_PROGRESS],
  ];

  const communicationSentFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract",
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.additionalIssueDescriptionText`,
      isHidden: ({ product }) =>
        product.advisoryHandling || !product.additionalIssueDescriptionText,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.additionalClaimText`,
      isHidden: ({ product }) =>
        product.advisoryHandling || !product.additionalClaimText,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.defectsDescriptionLawFirm`,
      isHidden: ({ product }) =>
        !_.has(product, "defectsDescriptionLawFirm") || product.caseCategory !== "enforceClaim",
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT],
  ];

  const reactionReceivedFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract",
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED],
  ];

  const actionRequiredFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_ACTION_REQUIRED],
  ];

  const prepareLawsuitFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract",
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_PREPARE_LAWSUIT],
  ];

  const lawsuitFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT],
  ];

  const lawsuitCompletedFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract",
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.courtReference`,
      validators: instituteReferenceDefault,
    },
    {
      type: "ValidatorDateField",
      validators: dateFieldDefault,
      path: `${product.productClassName}.judgementEebSentDate`,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.promulgationOfJudgementDate`,
      validators: dateFieldDefault,
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT_COMPLETED],
  ];

  const completedFields: FormElementPropsWithoutProduct<ContractUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract",
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
      isDisabled: () => {
        return !currentUser || !userHasOneOfTheseRoles(currentUser, [Roles.accounting, Roles.admin]);
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_COMPLETED],
  ];

  return {
    [stagesCase.STAGE_CREATED]: createdFields,
    [stagesCase.STAGE_DATA_COMPLETED]: dataCompletedFields,
    [stagesCase.STAGE_WAITING_FOR_FEEDBACK]: dataCompletedFields,
    [stagesCase.STAGE_DATA_COMPLETED_LAWFIRM]: dataLawFirmCompletedFields,
    [stagesCase.STAGE_CHECKED]: checkedFields,
    [stagesCase.STAGE_IN_PROGRESS]: inProgressFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]: communicationSentFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]: reactionReceivedFields,
    [stagesCase.STAGE_ACTION_REQUIRED]: actionRequiredFields,
    [stagesCase.STAGE_PREPARE_LAWSUIT]: prepareLawsuitFields,
    [stagesCase.STAGE_LAWSUIT]: lawsuitFields,
    [stagesCase.STAGE_TRIAL]: lawsuitFields,
    [stagesCase.STAGE_EVALUATE_JUDGEMENT]:
      abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EVALUATE_JUDGEMENT],
    [stagesCase.STAGE_LAWSUIT_COMPLETED]: lawsuitCompletedFields,
    [stagesCase.STAGE_COMPLETED]: completedFields,
  };
}
